import { ActivatedRoute, ActivatedRouteSnapshot, Data, NavigationEnd, ParamMap, Params, Router } from '@angular/router';
import { combineLatest, isObservable, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

/** Wird dem Provider als Parameter mitgegeben */
export interface IBreadcrumbRouteData {
  data: Data;
  paramMap: ParamMap;
}

/** Ergebnis Breadcrumb der DataSource */
export interface IBreadcrumb {
  label: string;
  routerLink: string[];
  queryParams?: Params;
}

export interface IBreadcrumbDataSource {
  /** Die aktuellen Breadcrumbs */
  breadcrumbs$: Observable<IBreadcrumb[]>;
}

/** Der Route Data Property Name, in dem die Breadcrumb Informationen stehen */
const ROUTE_DATA_KEY = 'breadcrumb';
/** Der Name der Provider Methode, wenn bei ROUTE_DATA_KEY ein Objekt angegeben wurde */
const PROVIDER_FUNCTION_NAME = 'getBreadcrumb';

export class BreadcrumbDataSource<T> implements IBreadcrumbDataSource {
  /** Die aktuellen Breadcrumbs */
  public breadcrumbs$: Observable<IBreadcrumb[]>;

  constructor(router: Router, activatedRoute: ActivatedRoute) {
    this.breadcrumbs$ = router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null as unknown),
      switchMap(() => this.getBreadcrumbs(activatedRoute.root.snapshot)),
      catchError((err) => {
        console.error(err);
        return of<IBreadcrumb[]>([]);
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  /**
   * Gibt die Breadcrumbs als sortiertes Array zurück
   *
   * @param route Die aktuell aktive Route
   * @param url Der Parent Url Part (für rekursive Aufrufe)
   * @param breadcrumbs$ Die Parent Breadcrumbs (für rekursive Aufrufe)
   * @returns Die Breadcrumbs als sortiertes Array
   */
  public getBreadcrumbs(route: ActivatedRouteSnapshot, url = '', breadcrumbs$: Observable<IBreadcrumb>[] = []): Observable<IBreadcrumb[]> {
    // get the routes URL segment
    const urlPart: string = route.url.map((segment) => segment.path).join('/');

    // append route URL to URL
    url += `/${urlPart}`;

    // get the breadcrumb provider
    const breadcrumbProvider = this.getProvider(route);

    // if no provider was found, we skip this breadcrumb
    if (breadcrumbProvider !== null) {
      // prepare the data and call the provider to get the breadcrumb text
      const breadcrumbRouteData: IBreadcrumbRouteData = {
        data: route.data,
        paramMap: route.paramMap,
      };
      const providedData = breadcrumbProvider(breadcrumbRouteData);
      const breadcrumb: Observable<IBreadcrumb> = (isObservable(providedData) ? providedData : of(providedData)).pipe(
        switchMap((data) => this.transform(data)),
        map(
          (label) =>
            ({
              label: label,
              routerLink: [url],
            }) as IBreadcrumb
        )
      );

      breadcrumbs$.push(breadcrumb);
    }

    if (route.firstChild) {
      return this.getBreadcrumbs(route.firstChild, url, breadcrumbs$);
    }

    return breadcrumbs$.length ? combineLatest(breadcrumbs$) : of([] as IBreadcrumb[]);
  }

  /**
   * Gibt für einen Route Part eine Funktion zurück, welche beim Aufrufen den Breadcrumb zurück gibt.
   * Dieser Breadcrumb wird dann nochmal an die transform Methode übergeben zum eventuellen Lokalisieren, etc.
   *
   * @param route Die aktuell aktive Route
   * @returns Die Breadcrumb Provider Funktion
   */
  public getProvider = (route: ActivatedRouteSnapshot): ((data: IBreadcrumbRouteData) => T | Observable<T>) | null => {
    if (route.routeConfig && route.routeConfig.data && Object.prototype.hasOwnProperty.call(route.routeConfig.data, ROUTE_DATA_KEY)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const breadcrumbConfig = route.routeConfig.data[ROUTE_DATA_KEY];
      if (breadcrumbConfig === null || breadcrumbConfig === undefined) {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (typeof breadcrumbConfig === 'function' && typeof breadcrumbConfig[PROVIDER_FUNCTION_NAME] === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return breadcrumbConfig[PROVIDER_FUNCTION_NAME];
      } else if (typeof breadcrumbConfig === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return breadcrumbConfig;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return () => breadcrumbConfig;
      }
    }

    return null;
  };

  /**
   * Transformiert den vom Provider zurückgegebenen Breadcrumb in den endgültigen Breadcrumb.
   * Kann z.B. zum Lokalisieren benutzt werden.
   *
   * @param breadcrumbData Der vom Provider zurückgegebene Breadcrumb
   * @returns Der endgültigen Breadcrumb
   */
  public transform = (breadcrumbData: T): Observable<string> => of(breadcrumbData + '');
}
